import React, { useState } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../config';

interface LocationState {
  email: string;
  message: string;
}

const RegistrationSuccess: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as LocationState;
  const [isResendingVerification, setIsResendingVerification] = useState(false);
  const [message, setMessage] = useState(state?.message || 'Registration successful. Please verify your email.');
  const [status, setStatus] = useState<'success' | 'error' | 'info'>('success');
  const email = state?.email || '';

  // Redirect to login if no state is provided (user shouldn't access this page directly)
  if (!state?.email && !state?.message) {
    navigate('/login');
    return null;
  }

  const handleResendVerification = async () => {
    if (!email) return;
    
    setIsResendingVerification(true);
    setStatus('info');
    setMessage('Sending verification email...');
    
    try {
      const resendUrl = `${API_BASE_URL}/auth/resend-verification`;
      const response = await fetch(resendUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setStatus('success');
        setMessage('Verification email has been resent. Please check your inbox.');
      } else {
        setStatus('error');
        setMessage(data.message || 'Failed to resend verification email');
      }
    } catch (err) {
      setStatus('error');
      setMessage('An error occurred while resending the verification email');
    } finally {
      setIsResendingVerification(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 mx-auto">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Registration Successful
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Please verify your email to continue
          </p>
        </div>
        
        <div className={`rounded-md ${
          status === 'error' ? 'bg-red-50' : 
          status === 'success' ? 'bg-green-50' : 
          'bg-blue-50'
        } p-4`}>
          <div className={`text-sm ${
            status === 'error' ? 'text-red-700' : 
            status === 'success' ? 'text-green-700' : 
            'text-blue-700'
          }`}>
            {message}
          </div>
          
          {email && (
            <div className="mt-3">
              <p className="text-sm text-gray-700">
                A verification email has been sent to: <span className="font-medium">{email}</span>
              </p>
              <p className="text-sm text-gray-700 mt-2">
                Please check your inbox and spam folder.
              </p>
            </div>
          )}
          
          <div className="mt-4">
            <button
              type="button"
              onClick={handleResendVerification}
              disabled={isResendingVerification}
              className="text-sm text-indigo-600 hover:text-indigo-500 font-medium focus:outline-none mr-4"
            >
              {isResendingVerification ? 'Sending...' : 'Resend verification email'}
            </button>
            
            <Link
              to="/login"
              className="text-sm text-indigo-600 hover:text-indigo-500 font-medium focus:outline-none"
            >
              Go to login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistrationSuccess;